import React, { useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken, toggleAttended } from '../attendedUtils';
import { normalizeStr, Pagination, ShowsByYearTab, VenuesVisitedTab, ArtistsWatchedTab, TopCitiesTab, ShowsMapTab } from './ShowStatsTabs';
import { FollowersFollowingTab } from './FollowersFollowingTab';

const TABS = ['Show History', 'Shows by Year', 'Venues Visited', 'Artists Watched', 'Top Cities', 'Shows Map', 'Followers'];
const PAGE_SIZE = 25;

// ── Public Show History Tab (My Role / User Role) ──────────────────────────────
function PublicShowHistoryTab({ shows, onMarkAttended }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [pendingIds, setPendingIds] = useState({}); // event_id -> true while a toggle is in flight

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;

    const q = normalizeStr(search);
    const filtered = search ? shows.filter(s =>
        [s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '',
         s.city, s.state, s.country, s.venue_name, s.artists, s.festival_name, s.role, s.viewer_role]
            .some(v => v && normalizeStr(v).includes(q))
    ) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const roleBadgeStyle = (role) => {
        if (role === 'Performed') return styles.rolePerformed;
        if (role === 'Worked') return styles.roleWorked;
        return styles.roleAttended;
    };

    const handleMarkAttended = async (eventId) => {
        setPendingIds(prev => ({ ...prev, [eventId]: true }));
        try {
            await onMarkAttended(eventId);
        } finally {
            setPendingIds(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
        }
    };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, city, venue, artists, role..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>My Role</th>
                                    <th style={styles.th}>User Role</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Artists</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isPending = !!pendingIds[show.event_id];
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={styles.td}>
                                                {show.viewer_role === 'Performed' ? (
                                                    <span style={styles.rolePerformed}>Performed</span>
                                                ) : show.viewer_role === 'Worked' ? (
                                                    <span style={styles.roleWorked}>Worked</span>
                                                ) : show.viewer_role === 'Attended' ? (
                                                    <span style={styles.roleAttended}>✓ Attended</span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleMarkAttended(show.event_id)}
                                                        disabled={isPending}
                                                        style={isPending ? { ...styles.markAttendedBtnOff, opacity: 0.5 } : styles.markAttendedBtnOff}
                                                    >
                                                        {isPending ? '…' : 'Attended?'}
                                                    </button>
                                                )}
                                            </td>
                                            <td style={styles.td}>
                                                <span style={roleBadgeStyle(show.role)}>{show.role}</span>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                                    : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.artists ? ` - ${show.artists}` : ''}</>
                                                    : show.artists || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── PublicProfile ────────────────────────────────────────────────────────────
function PublicProfile() {
    const { username } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // null | 'private' | 'not_found' | 'error'
    const [activeTab, setActiveTab] = useState(0);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followPending, setFollowPending] = useState(false);

    useEffect(() => {
        document.title = 'SHARC - Profile';
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    useEffect(() => {
        async function load() {
            setLoading(true);
            setStatus(null);
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/users/${username}/shows`, {
                    headers: { Authorization: token }
                });
                setData(res.data);
                setIsFollowing(!!res.data.viewer_is_following);
                setLoading(false);
            } catch (e) {
                const code = e.response && e.response.status;
                if (code === 403) setStatus('private');
                else if (code === 404) setStatus('not_found');
                else setStatus('error');
                setLoading(false);
            }
        }
        load();
    }, [username]);

    // Mark Attended from this page — reuses the existing toggle, then merges
    // the result back into show_history's viewer_role locally (optimistic).
    const handleMarkAttended = async (eventId) => {
        try {
            const result = await toggleAttended(eventId);
            setData(prev => {
                if (!prev) return prev;
                const nextRole = result.performed ? 'Performed' : result.worked ? 'Worked' : result.attended ? 'Attended' : null;
                return {
                    ...prev,
                    show_history: prev.show_history.map(s =>
                        s.event_id === eventId ? { ...s, viewer_role: nextRole } : s
                    ),
                };
            });
        } catch (e) {
            // Swallow — the button just stops spinning and stays clickable to retry.
        }
    };

    // Follow / Unfollow toggle — reuses the target's own username from the URL.
    const handleToggleFollow = async () => {
        setFollowPending(true);
        try {
            const token = await getIdToken();
            const res = await axios.post(`${config.API_URL}/users/${username}/follow`, {}, {
                headers: { Authorization: token }
            });
            setIsFollowing(!!res.data.following);
        } catch (e) {
            // Leave the button in its prior state on failure — safe to just retry the click.
        } finally {
            setFollowPending(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading profile...</div>;

    if (status === 'private') {
        return (
            <div style={styles.page}>
                <div style={styles.navRow}>
                    <Link to="/" style={styles.backLink}>← Home</Link>
                </div>
                <div style={styles.stateCard}>
                    <div style={styles.stateTitle}>This profile is private</div>
                    <div style={styles.stateHint}>The user has chosen not to share their show history publicly.</div>
                </div>
            </div>
        );
    }

    if (status === 'not_found') {
        return (
            <div style={styles.page}>
                <div style={styles.navRow}>
                    <Link to="/" style={styles.backLink}>← Home</Link>
                </div>
                <div style={styles.stateCard}>
                    <div style={styles.stateTitle}>No SHARC profile found for "{username}"</div>
                </div>
            </div>
        );
    }

    if (status === 'error' || !data) {
        return (
            <div style={styles.page}>
                <div style={styles.stateCard}>
                    <div style={styles.stateTitle}>Something went wrong loading this profile.</div>
                </div>
            </div>
        );
    }

    // Viewing your own public profile — send to the interactive My Shows page instead.
    if (data.is_own_profile) {
        return <Navigate to="/my-shows" replace />;
    }

    const { user, stats, show_history, venues_visited, artists_watched, geography, show_map, followers, following } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const termOfShows = (() => {
        if (!stats.first_show_date || !stats.last_show_date) return '—';
        const start = new Date(stats.first_show_date);
        const end = new Date(stats.last_show_date);
        let years = end.getUTCFullYear() - start.getUTCFullYear();
        let months = end.getUTCMonth() - start.getUTCMonth();
        let days = end.getUTCDate() - start.getUTCDate();
        if (days < 0) { months -= 1; days += new Date(end.getUTCFullYear(), end.getUTCMonth(), 0).getDate(); }
        if (months < 0) { years -= 1; months += 12; }
        const parts = [];
        if (years > 0) parts.push(`${years} Year${years !== 1 ? 's' : ''}`);
        if (months > 0) parts.push(`${months} Month${months !== 1 ? 's' : ''}`);
        if (days > 0 || parts.length === 0) parts.push(`${days} Day${days !== 1 ? 's' : ''}`);
        return parts.join(' ');
    })();

    const locationStr = [user.city, user.state, user.country].filter(Boolean).join(', ');

    return (
        <div style={styles.page}>
            {/* Nav */}
            <div style={styles.navRow}>
                <Link to="/" style={styles.backLink}>← Home</Link>
            </div>

            {/* Header */}
            <div style={styles.headerCard}>
                <div>
                    <h1 style={styles.title}>{user.display_name || user.username}</h1>
                    {locationStr && <div style={styles.locationStr}>{locationStr}</div>}
                    {user.member_name && (
                        <div style={styles.memberBadge}>
                            Member of <Link to={`/members/${user.member_slug}`} style={styles.memberLink}>{user.member_name}</Link>
                        </div>
                    )}
                    <div style={styles.actionRow}>
                        <button
                            onClick={handleToggleFollow}
                            disabled={followPending}
                            style={{
                                ...(isFollowing ? styles.followingBtn : styles.followBtn),
                                opacity: followPending ? 0.6 : 1,
                            }}
                        >
                            {isFollowing ? 'Following' : '+ Follow'}
                        </button>
                        <button style={styles.contactBtn}>Contact</button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_shows}</div>
                    <div style={styles.statLabel}>Total Shows</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.attended_shows}</div>
                    <div style={styles.statLabel}>Attended</div>
                </div>
                {stats.performed_shows > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{stats.performed_shows}</div>
                        <div style={styles.statLabel}>Performed</div>
                    </div>
                )}
                {stats.worked_shows > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{stats.worked_shows}</div>
                        <div style={styles.statLabel}>Worked</div>
                    </div>
                )}
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{venues_visited.length}</div>
                    <div style={styles.statLabel}>Venues Visited</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.first_show_date ? formatDate(stats.first_show_date) : '—'}</div>
                    <div style={styles.statLabel}>First Show</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.last_show_date ? formatDate(stats.last_show_date) : '—'}</div>
                    <div style={styles.statLabel}>Most Recent Show</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{termOfShows}</div>
                    <div style={styles.statLabel}>Term of Shows</div>
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(i)}
                            style={{ ...styles.tabBtn, ...(activeTab === i ? styles.tabBtnActive : {}) }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeTab === 0 && <PublicShowHistoryTab shows={show_history} onMarkAttended={handleMarkAttended} />}
                    {activeTab === 1 && <ShowsByYearTab shows={show_history} />}
                    {activeTab === 2 && <VenuesVisitedTab venues={venues_visited} />}
                    {activeTab === 3 && <ArtistsWatchedTab artists={artists_watched} />}
                    {activeTab === 4 && <TopCitiesTab geography={geography} />}
                    {activeTab === 5 && <ShowsMapTab showMap={show_map} />}
                    {activeTab === 6 && <FollowersFollowingTab followers={followers} following={following} />}
                </div>
            </div>
        </div>
    );
}

const styles = {
    page: {},
    navRow: { marginBottom: '24px' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    headerCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', padding: '28px', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: '0 0 8px 0' },
    locationStr: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },
    memberBadge: { fontSize: '13px', color: '#888', marginBottom: '16px' },
    memberLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    actionRow: { display: 'flex', gap: '12px', marginTop: '16px' },
    followBtn: { backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 18px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    followingBtn: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '6px', padding: '10px 18px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    contactBtn: { backgroundColor: '#2c5a70', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 18px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '40px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '22px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '12px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', flexWrap: 'wrap', borderBottom: '2px solid #2a2a2a', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
    tabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', color: '#e0e0e0', verticalAlign: 'middle' },
    trEven: { backgroundColor: 'transparent' },
    trOdd: { backgroundColor: '#1e1e1e' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    roleAttended: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    rolePerformed: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    roleWorked: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    markAttendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    emptyTab: { color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '24px 0' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    stateCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', padding: '48px 28px', textAlign: 'center' },
    stateTitle: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '8px' },
    stateHint: { fontSize: '14px', color: '#888' },
};

export default PublicProfile;
